import { Injectable } from '@angular/core';

import { Subject } from 'rxjs';

import { ToastService, TranslationService } from '@mirage-projects/cw-commons';
import { Property, PropertySet } from '@mirage-projects/cw-models';

import { MessageDialogOptions, MessageDialogService } from '../message-dialog/message-dialog.service';

/**
 * This service implements the business logic associated with the property bag
 */
@Injectable()
export class PropertyBagService {
    value = [];

    private propertyChangedSubject$ = new Subject<Property>();
    readonly propertyChanged$ = this.propertyChangedSubject$.asObservable();

    private stylesChangedSubject$ = new Subject<PropertySet>();
    readonly stylesChanged$ = this.stylesChangedSubject$.asObservable();

    constructor(
        private toastService: ToastService,
        private translationService: TranslationService,
        private messageDialogService: MessageDialogService
    ) {}

    getValueProperty(propertySet: PropertySet, property: Property, args?: any): any {
        return propertySet.getProperty(property.field, args);
    }

    setValueProperty(propertySet: PropertySet, property: Property, optionalArgs?: any): void {
        propertySet.setProperty(property.field, property.value, optionalArgs);
        this.emmitPropertyValue(property);
    }

    emmitPropertyValue(property: Property): void {
        this.propertyChangedSubject$.next(property);
    }

    getValueRange(propertySet: PropertySet, property: Property): any {
        return propertySet.getValueRange(property.field);
    }

    async deleteRule(propertySet: PropertySet, property: Property): Promise<void> {
        const options: Partial<MessageDialogOptions> = {
            headerText: 'VECTOR_LAYER_PROPERTIES.DELETE_RULE_CONFIRM_TITLE',
            contentText: 'VECTOR_LAYER_PROPERTIES.DELETE_RULE_CONFIRM_MSG',
            buttonsList: [this.messageDialogService.getCancelButtonConfig(), this.messageDialogService.getOkButtonConfig()],
        };

        const button = await this.messageDialogService.showWarning(options);
        if (button.id === 'ok') {
            const ok = propertySet.deleteStyleRule(property.field);
            if (!ok) {
                this.toastService.error('VECTOR_LAYER_PROPERTIES.RULE_REMOVE_FAILED');
                return;
            }
            this.toastService.generic('VECTOR_LAYER_PROPERTIES.RULE_REMOVED');
            this.setValueProperty(propertySet, { field: 'styleGroup' } as Property);
            this.emitStylesChanged(propertySet);
        }
    }

    addRule(propertySet: PropertySet): void {
        const ok = propertySet.addStyleRule();
        if (!ok) {
            this.toastService.error('VECTOR_LAYER_PROPERTIES.RULE_ADD_FAILED');
            return;
        }
        this.toastService.generic('VECTOR_LAYER_PROPERTIES.RULE_ADDED');
        this.setValueProperty(propertySet, { field: 'styleGroup' } as Property);
        this.emitStylesChanged(propertySet);
    }

    /**
     * Checks if the property label is a feature property (checking if the resource exists) or not, and returns the valid property label text/resource
     * @param property property to check
     * @returns
     */
    getCorrectLabel(property: Property): string {
        if (!property.label) {
            return '';
        }
        const keyFeature = `FEATURE_PROPERTIES.${property.label}`;
        return this.translationService.existKey(keyFeature) ? keyFeature : property.label;
    }

    private emitStylesChanged(propertySet: PropertySet): void {
        this.stylesChangedSubject$.next(propertySet);
    }
}
